import config from '../../../../../config';
import { FC, useState, useEffect } from 'react';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Models
import IBillingInvoice from 'app/models/BillingInvoice';
// Async
import { batchChargeInvoices, chargeInvoice } from 'app/store/BillingCharges/BillingCharges.async';
// Selectors
import { selectBatchSentInvoices } from 'app/store/BillingInvoices/BillingInvoices.selectors';
// import { selectLoading } from 'app/store/BillingCharges/BillingCharges.selectors';
// Mui
import { Alert } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import { Button } from 'app/components/Mui';
// i18next
import { useTranslation } from 'react-i18next';
// Mixpanel
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';
// Utilities
import { toNumberWithCommas } from 'app/utilities/Utilities';

interface IError {
  code: string;
  message: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  invoice?: IBillingInvoice;
};

const CreditCardPaymentFormDialog:FC<Props> = ({
  // Props
  open, onClose, invoice
}) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const batchInvoices = useAppSelector(selectBatchSentInvoices);
  // const loading = useAppSelector(selectLoading);

  const [ loaded, setLoaded ] = useState(false);
  const [ errors, setErrors ] = useState<IError[]>([]);

  useEffect(() => {
    loadHeartlandScript(() => {
      setLoaded(true);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( loaded ){
      const amountPay = invoice
        ? invoice.amount
        : batchInvoices.reduce((total, inv) => total + inv.amount, 0);

      (window as any).GlobalPayments.configure({
        publicApiKey: config.billingPublicApiKey
      });
      // Create Form
      const cardForm = (window as any).GlobalPayments.creditCard.form('#credit-card');
      // Customize Form
      cardForm.fields.submit.text = `${t('buttons.pay')} $${toNumberWithCommas(amountPay)}`;
      cardForm.styles['#secure-payment-field[type=button]']['border-radius'] = '4px';
      cardForm.styles['#secure-payment-field[type=button]']['background-color'] = '#6464e6';
      cardForm.styles['#secure-payment-field[type=button]:hover']['background-color'] = '#4646a1';
      cardForm.styles['#secure-payment-field[type=button]:focus']['background-color'] = '#4646a1';
      
      cardForm.on('token-success', (response:any) => {
        setErrors([]);
        if ( response.paymentReference ){
          const card = { token: response.paymentReference };
          if ( invoice ){
            asyncChargeInvoice({ card, invoiceId: invoice.id });
          } else {
            asyncBatchChargeInvoice({ card, invoiceIds: batchInvoices.map(inv => inv.id) });
          }
        }
      });
      cardForm.on('token-error', (response:any) => {
        if ( response.error ) setErrors(response.reasons);
      });
      const style = document.getElementById('secure-payment-styles-default');
      if (style) style.remove();
    }
    // eslint-disable-next-line
  }, [loaded]);

  const asyncChargeInvoice = async (data:any) => {
    try {
      await dispatch(chargeInvoice(data)).unwrap();
      dispatch(MixpanelActions.handleBillingInvoicesCardPaymentClick({ invoiceId: data.invoiceId }));
      onClose();
    } catch(error){}
  }

  const asyncBatchChargeInvoice = async (data:any) => {
    try {
      await dispatch(batchChargeInvoices(data)).unwrap();
      dispatch(MixpanelActions.handleBillingInvoicesBulkCardPaymentClick({ invoicesIds: data.invoiceIds }));
      onClose();
    } catch(error){}
  }

  const actions = (
    <Button
      name="Cancel credit card payment dialog"
      onClick={onClose}
    >{t('buttons.close')}</Button>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('pages.billingAndSubscriptions.creditCardPaymentFormTitle')}
      actions={actions}
    >
      {errors && errors.length > 0 ? (
        errors.map((error:IError, index:number) => (
          <Alert key={`error-item-${index}`} severity="error">{error.message}</Alert>
        ))
      ) : null}
      {loaded ? (
        <form id="payment-form" action="/Payment/Charge" method="get">
          <div id="credit-card"></div>
        </form>
      ) : null}
    </Dialog>
  )
}

const loadHeartlandScript = (callback:() => void) => {
  const existingScript = document.getElementById('react_heartland');
  if ( !existingScript ){
    const script = document.createElement('script');
    script.src = 'https://api2.heartlandportico.com/SecureSubmit.v1/token/gp-1.0.1/globalpayments.js';
    script.id = 'react_heartland';
    document.body.appendChild(script);
    script.onload = () => { 
      if ( callback ) callback();
    };
  }
  if ( existingScript && callback ) callback();
}

export default CreditCardPaymentFormDialog;
